import React from 'react';
import cx from 'classnames';

import Touchable from '../Touchable';
import Text from '../Text';
import ChevronDownS1 from '../Icons/ChevronDownS1';
import ChevronUpS1 from '../Icons/ChevronUpS1';
import AccordionGroup, { AccordionGroupProps } from './AccordionGroup';

import { useUniqueId } from '../utils';

import styles from './Accordion.module.scss';

export interface AccordionProps {
  /** Child of `Accordion.Item`. */
  children?: React.ReactNode;
  /** Apply CSS className for `Accordion.Item` container. */
  className?: Parameters<typeof cx>[0];
  /** Visible name of the `Accordion.Item`. */
  label: string | React.ReactNode;
  /** If true, `Accordion.Item` is expanded. */
  isExpanded: boolean;
  /**	Function that will be called when the `Accordion.Item` is clicked. */
  onChange: () => void;
}

export interface AccordionComposite {
  Group: React.FC<AccordionGroupProps>;
  Item: React.FC<AccordionProps>;
}

export const Accordion: React.FC<AccordionProps> & AccordionComposite = ({
  children,
  className,
  label,
  isExpanded,
  onChange,
}) => {
  const id = useUniqueId('accordion');

  return (
    <div className={cx(styles.accordionItem, className)}>
      <Touchable
        className={styles.accordionButton}
        aria-expanded={isExpanded}
        ariaControls={`${id}-panel`}
        onClick={onChange}
      >
        {typeof label === 'string' ? (
          <Text
            id={`${id}-label`}
            size={16}
            weight='semiBold'
            className={styles.text}
          >
            {label}
          </Text>
        ) : (
          label
        )}
        <div className={styles.iconGroup}>
          {isExpanded ? (
            <ChevronUpS1 className={styles.icon} />
          ) : (
            <ChevronDownS1 className={styles.icon} />
          )}
        </div>
      </Touchable>
      {isExpanded && (
        <div
          id={`${id}-panel`}
          aria-labelledby={`${id}-label`}
          className={styles.panel}
        >
          {children}
        </div>
      )}
    </div>
  );
};

Accordion.Item = Accordion;
Accordion.Group = AccordionGroup;

export default Accordion;
export { styles };
